import { createServerFn } from "@tanstack/react-start";
import { getAuthContext, getOrCreateAccount, computeBalances } from "@/lib/services";
import { computeSplits } from "@/lib/types";
import type {
  FriendView,
  ExpenseView,
  ExpenseSplitView,
  ActivityItem,
  BalanceView,
} from "@/lib/types";
import {
  listExpenses,
  getSplitsForExpense,
  deleteExpense as dbDeleteExpense,
  getExpenseForUser,
  createExpense as dbCreateExpense,
  createBulkExpenses as dbCreateBulkExpenses,
  updateExpense as dbUpdateExpense,
} from "@/db/expense";
import type { BulkExpenseInput } from "@/db/expense";
import { listActivity } from "@/db/activity";
import type { ActivityRow, ActivityCursor, ActivityFilters, ActivitySortBy } from "@/db/activity";

export type { ActivityFilters, ActivitySortBy };
import { listFriends } from "@/db/friend";
import { getTransfer, createTransfer } from "@/db/pgledger";
import { getUserSettings } from "@/db/user";
import type { CreateSplit } from "@/db/types";

export const PAGE_SIZE = 20;

// ── Cursor helpers ──────────────────────────────────────────────────

function encodeCursor(row: ActivityRow, sortBy: ActivitySortBy): string {
  const sortValue =
    sortBy === "created_at"
      ? new Date(row.created_at).toISOString()
      : new Date(row.activity_date).toISOString();
  return btoa(
    JSON.stringify({
      v: sortValue,
      t: row.activity_type,
      i: row.activity_id,
    }),
  );
}

function decodeCursor(cursor: string): ActivityCursor {
  const parsed = JSON.parse(atob(cursor)) as { v: string; t: string; i: number };
  return { sortValue: parsed.v, activityType: parsed.t, activityId: parsed.i };
}

function rowToActivityItem(row: ActivityRow): ActivityItem {
  if (row.activity_type === "expense") {
    const splits: ExpenseSplitView[] = (row.splits_json ?? []).map((s) => ({
      userId: s.userId,
      amount: s.amount,
      percentage: s.percentage ?? undefined,
      shares: s.shares ?? undefined,
    }));
    const expense: ExpenseView = {
      id: row.activity_id.toString(),
      description: row.description ?? "",
      amount: row.amount,
      currency: row.currency,
      paidBy: row.paid_by ?? "",
      splitType: row.split_type ?? "",
      splits,
      date: new Date(row.activity_date).toISOString().split("T")[0],
    };
    return { type: "expense", date: expense.date, item: expense };
  }

  return {
    type: "settlement",
    date: new Date(row.activity_date).toISOString().split("T")[0],
    item: {
      id: row.activity_id.toString(),
      fromUserId: row.from_user_id ?? "",
      toUserId: row.to_user_id ?? "",
      amount: row.amount,
      currency: row.currency,
      date: new Date(row.activity_date).toISOString().split("T")[0],
    },
  };
}

// ── Server functions ────────────────────────────────────────────────

export const loadExpenses = createServerFn({ method: "GET" }).handler(async () => {
  const { db, userId, email, name } = await getAuthContext();

  try {
    const [expenseRows, friendRows] = await Promise.all([
      listExpenses(db, userId, null, 50, null),
      listFriends(db, userId),
    ]);

    const expenses: ExpenseView[] = await Promise.all(
      expenseRows.map(async (e) => {
        const splitRows = await getSplitsForExpense(db, e.id);
        const splits: ExpenseSplitView[] = splitRows.map((s) => ({
          userId: s.user_id,
          amount: s.amount,
          percentage: s.percentage ?? undefined,
          shares: s.shares ?? undefined,
        }));
        return {
          id: e.id.toString(),
          description: e.description,
          amount: e.amount,
          currency: e.currency,
          paidBy: e.paid_by,
          splitType: e.split_type,
          splits,
          date: new Date(e.date).toISOString().split("T")[0],
        };
      }),
    );

    const friends: FriendView[] = friendRows.map((f) => ({
      id: f.id.toString(),
      userId: f.user_id,
      user: { id: f.user_id, email: f.email, name: f.name },
    }));

    return {
      expenses,
      friends,
      currentUser: { id: userId, email, name },
    };
  } catch (error) {
    console.error("Failed to load expenses:", error);
    return {
      expenses: [] as ExpenseView[],
      friends: [] as FriendView[],
      currentUser: { id: userId, email, name },
    };
  }
});

export const loadActivity = createServerFn({ method: "GET" })
  .inputValidator((data: { filters?: ActivityFilters } | undefined) => data)
  .handler(async ({ data }) => {
    const { db, userId, email, name } = await getAuthContext();
    const filters = data?.filters ?? {};

    try {
      const [activityRows, balanceData, friendRows] = await Promise.all([
        listActivity(db, userId, PAGE_SIZE + 1, null, filters),
        computeBalances(db, userId),
        listFriends(db, userId),
      ]);

      const hasMore = activityRows.length > PAGE_SIZE;
      const pagedRows = hasMore ? activityRows.slice(0, PAGE_SIZE) : activityRows;

      const items: ActivityItem[] = pagedRows.map(rowToActivityItem);
      const nextCursor = hasMore
        ? encodeCursor(activityRows[PAGE_SIZE - 1], filters.sortBy ?? "date")
        : null;

      const friends: FriendView[] = friendRows.map((f) => ({
        id: f.id.toString(),
        userId: f.user_id,
        user: { id: f.user_id, email: f.email, name: f.name },
      }));

      return {
        items,
        nextCursor,
        balances: balanceData.balances,
        totals: balanceData.totals,
        friends,
        currentUser: { id: userId, email, name },
      };
    } catch (error) {
      console.error("Failed to load activity:", error);
      return {
        items: [] as ActivityItem[],
        nextCursor: null as string | null,
        balances: [] as BalanceView[],
        totals: [] as BalanceView[],
        friends: [] as FriendView[],
        currentUser: { id: userId, email, name },
      };
    }
  });

export const loadMoreActivityFn = createServerFn({ method: "GET" })
  .inputValidator((data: { cursor: string; limit: number; filters?: ActivityFilters }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();

    const cursor = decodeCursor(data.cursor);
    const activityRows = await listActivity(db, userId, data.limit + 1, cursor, data.filters ?? {});
    const hasMore = activityRows.length > data.limit;
    const pagedRows = hasMore ? activityRows.slice(0, data.limit) : activityRows;

    const items: ActivityItem[] = pagedRows.map(rowToActivityItem);
    const nextCursor = hasMore
      ? encodeCursor(activityRows[data.limit - 1], data.filters?.sortBy ?? "date")
      : null;

    return { items, nextCursor };
  });

export const deleteExpenseFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();

    const expenseId = Number(data.id);

    if (!(await getExpenseForUser(db, expenseId, userId))) {
      throw new Error("Expense not found");
    }

    const transferIds = await dbDeleteExpense(db, expenseId);

    for (const transferId of transferIds) {
      const original = await getTransfer(db, transferId);
      if (original) {
        await createTransfer(
          db,
          original.to_account_id,
          original.from_account_id,
          original.amount,
          null,
          { reversal_of: transferId, deleted_expense_id: expenseId },
        );
      }
    }
  });

export const loadExpenseDetail = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const { db, userId, email, name } = await getAuthContext();

    const [expenseRow, friendRows] = await Promise.all([
      getExpenseForUser(db, Number(data.id), userId),
      listFriends(db, userId),
    ]);

    if (!expenseRow) {
      throw new Error("Expense not found");
    }

    const splitRows = await getSplitsForExpense(db, expenseRow.id);

    const splits: ExpenseSplitView[] = splitRows.map((s) => ({
      userId: s.user_id,
      amount: s.amount,
      percentage: s.percentage ?? undefined,
      shares: s.shares ?? undefined,
    }));

    const expense: ExpenseView = {
      id: expenseRow.id.toString(),
      description: expenseRow.description,
      amount: expenseRow.amount,
      currency: expenseRow.currency,
      paidBy: expenseRow.paid_by,
      splitType: expenseRow.split_type,
      splits,
      date: new Date(expenseRow.date).toISOString().split("T")[0],
    };

    const friends: FriendView[] = friendRows.map((f) => ({
      id: f.id.toString(),
      userId: f.user_id,
      user: { id: f.user_id, email: f.email, name: f.name },
    }));

    return {
      expense,
      friends,
      currentUser: { id: userId, email, name },
    };
  });

export const loadNewExpense = createServerFn({ method: "GET" }).handler(async () => {
  const { db, userId, email, name } = await getAuthContext();

  try {
    const [friendRows, settings] = await Promise.all([
      listFriends(db, userId),
      getUserSettings(db, userId),
    ]);
    const friends: FriendView[] = friendRows.map((f) => ({
      id: f.id.toString(),
      userId: f.user_id,
      user: { id: f.user_id, email: f.email, name: f.name },
    }));
    return {
      friends,
      currentUser: { id: userId, email, name },
      defaultCurrency: settings.default_currency,
    };
  } catch (error) {
    console.error("Failed to load data:", error);
    return {
      friends: [] as FriendView[],
      currentUser: { id: userId, email, name },
      defaultCurrency: "CAD",
    };
  }
});

export const createExpenseFn = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      description: string;
      amount: string;
      currency: string;
      paidBy: string;
      splitType: string;
      splits: ExpenseSplitView[];
      date: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { db } = await getAuthContext();

    const computed = computeSplits(data.splits, data.amount, data.splitType);

    const payerAccount = await getOrCreateAccount(db, data.paidBy, data.currency);

    const dbSplits: CreateSplit[] = [];
    for (const split of computed) {
      let transferId: string | null = null;

      if (split.userId !== data.paidBy && parseFloat(split.amount) > 0) {
        const debtorAccount = await getOrCreateAccount(db, split.userId, data.currency);
        const transfer = await createTransfer(
          db,
          debtorAccount.pgledger_account_id,
          payerAccount.pgledger_account_id,
          split.amount,
          null,
          null,
        );
        transferId = transfer.id;
      }

      dbSplits.push({
        user_id: split.userId,
        amount: split.amount,
        percentage: split.percentage ?? null,
        shares: split.shares ?? null,
        transfer_id: transferId,
      });
    }

    await dbCreateExpense(
      db,
      data.description,
      data.amount,
      data.currency,
      data.paidBy,
      data.splitType,
      data.date,
      dbSplits,
    );
  });

export const updateExpenseFn = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      id: string;
      description: string;
      amount: string;
      currency: string;
      paidBy: string;
      splitType: string;
      splits: ExpenseSplitView[];
      date: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { db, userId } = await getAuthContext();

    const expenseId = Number(data.id);

    if (!(await getExpenseForUser(db, expenseId, userId))) {
      throw new Error("Expense not found");
    }

    // Fetch old transfer IDs so we can reverse them
    const transferResult = await db.query<{ transfer_id: string }>(
      `SELECT transfer_id FROM expense_splits WHERE expense_id = $1 AND transfer_id IS NOT NULL`,
      [expenseId],
    );
    const oldTransferIds = transferResult.rows.map((r) => r.transfer_id);

    // Reverse old ledger transfers
    for (const transferId of oldTransferIds) {
      const original = await getTransfer(db, transferId);
      if (original) {
        await createTransfer(
          db,
          original.to_account_id,
          original.from_account_id,
          original.amount,
          null,
          {
            reversal_of: transferId,
            edited_expense_id: expenseId,
          },
        );
      }
    }

    // Compute new splits
    const computed = computeSplits(data.splits, data.amount, data.splitType);

    // Create new pgledger transfers for the updated splits
    const payerAccount = await getOrCreateAccount(db, data.paidBy, data.currency);

    const dbSplits: CreateSplit[] = [];
    for (const split of computed) {
      let transferId: string | null = null;

      if (split.userId !== data.paidBy && parseFloat(split.amount) > 0) {
        const debtorAccount = await getOrCreateAccount(db, split.userId, data.currency);
        const transfer = await createTransfer(
          db,
          debtorAccount.pgledger_account_id,
          payerAccount.pgledger_account_id,
          split.amount,
          null,
          null,
        );
        transferId = transfer.id;
      }

      dbSplits.push({
        user_id: split.userId,
        amount: split.amount,
        percentage: split.percentage ?? null,
        shares: split.shares ?? null,
        transfer_id: transferId,
      });
    }

    // Update expense record and replace splits in a single transaction
    await dbUpdateExpense(
      db,
      expenseId,
      data.description,
      data.amount,
      data.currency,
      data.paidBy,
      data.splitType,
      data.date,
      dbSplits,
    );
  });

export const createBulkExpensesFn = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      rows: { description: string; amount: string; date: string }[];
      currency: string;
      paidBy: string;
      splitType: string;
      splits: ExpenseSplitView[];
    }) => data,
  )
  .handler(async ({ data }) => {
    const { db } = await getAuthContext();

    const payerAccount = await getOrCreateAccount(db, data.paidBy, data.currency);

    // Pre-create debtor accounts for all split participants
    const debtorAccounts = new Map<string, string>();
    for (const split of data.splits) {
      if (split.userId !== data.paidBy) {
        const account = await getOrCreateAccount(db, split.userId, data.currency);
        debtorAccounts.set(split.userId, account.pgledger_account_id);
      }
    }

    const bulkInputs: BulkExpenseInput[] = [];
    for (const row of data.rows) {
      const computed = computeSplits(data.splits, row.amount, data.splitType);

      const dbSplits: CreateSplit[] = [];
      for (const split of computed) {
        let transferId: string | null = null;

        if (split.userId !== data.paidBy && parseFloat(split.amount) > 0) {
          const debtorAccountId = debtorAccounts.get(split.userId)!;
          const transfer = await createTransfer(
            db,
            debtorAccountId,
            payerAccount.pgledger_account_id,
            split.amount,
            null,
            null,
          );
          transferId = transfer.id;
        }

        dbSplits.push({
          user_id: split.userId,
          amount: split.amount,
          percentage: split.percentage ?? null,
          shares: split.shares ?? null,
          transfer_id: transferId,
        });
      }

      bulkInputs.push({
        description: row.description,
        amount: row.amount,
        date: row.date,
        splits: dbSplits,
      });
    }

    await dbCreateBulkExpenses(db, bulkInputs, data.currency, data.paidBy, data.splitType);
  });
